(() => {
  let manifestPromise;
  let corePromise;
  const styles = new Map();

  async function getBase() {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      if (window.jdgm?.CDN_BASE_URL && typeof window.jdgm.loadScript === 'function') return window.jdgm.CDN_BASE_URL;
      await new Promise(resolve => setTimeout(resolve, 250));
    }
    throw new Error('Review app is unavailable');
  }

  function getCore() {
    if (typeof window.jdgm?.$ === 'function') return Promise.resolve();
    if (!corePromise) {
      corePromise = new Promise((resolve, reject) => {
        const finish = () => {
          clearTimeout(timeout);
          document.removeEventListener('jdgm.finishLoadingCore', finish);
          resolve();
        };
        const timeout = setTimeout(() => {
          document.removeEventListener('jdgm.finishLoadingCore', finish);
          reject(new Error('Review app core is unavailable'));
        }, 15000);
        document.addEventListener('jdgm.finishLoadingCore', finish, {once: true});
        const requested = window.jdgm.loadScript.requestedUrls || [];
        if (requested.some(url => /widget(?:\/|_)base\.js/.test(url))) return;
        window.jdgm.loadScript('widget/base.js', () => {
          window.jdgm.loadScript('widget/common.js', () => {
            window.jdgm.triggerVanillaEvent('finishLoadingCore');
            finish();
          });
        });
      }).catch(error => { corePromise = null; throw error; });
    }
    return corePromise;
  }

  function loadStyle(href) {
    if (styles.has(href)) return styles.get(href);
    if (Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(link => link.href === href)) return Promise.resolve();
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      const timeout = setTimeout(() => link.onerror(), 15000);
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = () => { clearTimeout(timeout); resolve(); };
      link.onerror = () => {
        clearTimeout(timeout);
        link.remove();
        styles.delete(href);
        reject(new Error('Review styles are unavailable'));
      };
      document.head.appendChild(link);
    });
    styles.set(href, promise);
    return promise;
  }

  async function getClient(base) {
    if (!manifestPromise) {
      manifestPromise = fetch(new URL('manifest.json', base), {signal: AbortSignal.timeout(15000)})
        .then(response => {
          if (!response.ok) throw new Error('Review manifest is unavailable');
          return response.json();
        }).catch(error => { manifestPromise = null; throw error; });
    }
    const manifest = await manifestPromise;
    const entry = manifest['review-widget/ReviewWidgetManager.js'];
    if (!entry?.file) throw new Error('Review client is unavailable');
    const visited = new Set();
    const css = new Set();
    function collect(key) {
      if (visited.has(key)) return;
      visited.add(key);
      const chunk = manifest[key];
      chunk?.css?.forEach(file => css.add(new URL(file, base).href));
      chunk?.imports?.forEach(collect);
    }
    collect('review-widget/main.js');
    await Promise.all(Array.from(css, loadStyle));
    return import(new URL(entry.file, base).href);
  }

  function waitForButton(widget) {
    const selector = '[data-testid="write-review-button"], .jdgm-write-rev-link';
    const existing = widget.querySelector(selector);
    if (existing) return Promise.resolve(existing);
    return new Promise((resolve, reject) => {
      const observer = new MutationObserver(() => {
        const button = widget.querySelector(selector);
        if (!button) return;
        clearTimeout(timeout);
        observer.disconnect();
        resolve(button);
      });
      const timeout = setTimeout(() => {
        observer.disconnect();
        reject(new Error('Review form is unavailable'));
      }, 15000);
      observer.observe(widget, {childList: true, subtree: true});
    });
  }

  async function mount(container, open = false) {
    container.rkOpenReview = container.rkOpenReview || open;
    if (container.dataset.rkComposeLoaded === 'true') return;
    if (container.dataset.rkComposeLoading === 'true') return;
    container.dataset.rkComposeLoading = 'true';
    container.setAttribute('aria-busy', 'true');
    const trigger = container.querySelector('[data-rk-review-compose-trigger]');
    try {
      const base = await getBase();
      const baseStyle = window.jdgm.isVersion3 ? 'widget_v3_base.css' : 'widget_base.css';
      const [client] = await Promise.all([getClient(base), getCore(), loadStyle(new URL(baseStyle, base).href)]);
      const template = container.querySelector('template[data-rk-review-compose-template]');
      if (template) {
        container.appendChild(template.content.cloneNode(true));
        template.remove();
      }
      const widget = container.querySelector('.jdgm-review-widget');
      await new client.ReviewWidgetManager(widget).initialize();
      const nativeButton = await waitForButton(widget);
      container.dataset.rkComposeLoaded = 'true';
      trigger.hidden = true;
      if (container.rkOpenReview) nativeButton.click();
    } catch (error) {
      console.error('RomKart review form:', error);
    } finally {
      container.dataset.rkComposeLoading = 'false';
      container.removeAttribute('aria-busy');
    }
  }

  function start() {
    document.querySelectorAll('[data-rk-review-compose]').forEach(container => {
      if (container.dataset.rkComposeReady === 'true') return;
      container.dataset.rkComposeReady = 'true';
      container.querySelector('[data-rk-review-compose-trigger]').addEventListener('click', () => mount(container, true));
      const parameters = new URLSearchParams(location.search);
      const reviewLink = /judgeme|judgeme_product_reviews/.test(location.hash) || ['judgeme_token', 'judgeme_review_uuid', 'judgeme_dynamic_form', 'judgeme_follow_up_token', 'judgeme_upload_pictures', 'judgeme_pre_verified'].some(key => parameters.has(key));
      if (reviewLink || window.Shopify?.designMode) mount(container);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
  document.addEventListener('shopify:section:load', start);
})();
